import React, { useState, useEffect } from "react"
import { maintenanceUpdate } from "../../../api/maintenance_update"
import { getMaintenance } from "../../../api/get_maintenance"
import { getMonthlyReports } from "../../../api/get_monthly_reports"
import { getDailyReports } from "../../../api/get_daily_reports"
import { changeViewMode } from "../../../api/change_view_mode"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../LoadingContext"
import { useConnectionError } from "../../ConnectionErrorContext"
import DashboardLayout from "../../components/DashboardLayout"
import DashboardNavbar from "../../components/DashboardNavbar"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import MDInput from "components/MDInput"
import { Card } from "@mui/material"
import { getManageCatvList } from "../../../api/manage_catv_list"
import { ManageCatvData } from "../catv"
import { getManageEstateList } from "../../../api/manage_estate_list"
import { ManageEstateData } from "../estate"
import ManageAdminPassword from "../password/admin"
import { Icon } from "@mui/material"

export interface MonthlyReportData {
  filename: string
  year_month: string
  url: string
}
export interface DailyReportData {
  filename: string
  date: string
  url: string
}
const ManagePage: React.FC = () => {
  const navigate = useNavigate()

  // 通信関連
  const { startLoading, stopLoading } = useLoading()
  const { showConnectionError } = useConnectionError()

  // メンテナンスモード関連
  const [maintenance, setMaintenance] = useState(0)
  const [monthlyReport, setMonthlyReport] = useState<MonthlyReportData[]>([])
  const [dailyReport, setDailyReport] = useState<DailyReportData[]>([])
  const [maintenanceMemo, setMaintenanceMemo] = useState("")

  // 閲覧モード関連
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [catvData, setCatvData] = useState<ManageCatvData[]>([])
  const [showModeCatv, setShowModeCatv] = useState<string>("")
  const [showModeCatvName, setShowModeCatvName] = useState<string>("")
  const [estateData, setEstateData] = useState<ManageEstateData[]>([])
  const [filteredModeEstate, setFilteredModeEstate] = useState<ManageEstateData[]>([])
  const [showModeEstate, setShowModeEstate] = useState<string>("")
  const [showModeEstateName, setShowModeEstateName] = useState<string>("")
  const [viewMode, setViewMode] = useState<string>("")
  const [searchStartDate, setSearchStartDate] = useState("")
  const [searchEndDate, setSearchEndDate] = useState("")

  // 未ログインの場合はエラー
  if (localStorage.getItem("session_code") == "") {
    navigate("/login")
    return <></>
  }

  // 開発者アカウント以外でログインした場合はエラー
  const auth = localStorage.getItem("account_type")
  if (auth != "service") {
    navigate("/") // 不正アクセス
  }

  // 初期表示時の処理
  const fetchMaintenance = async () => {
    try {
      startLoading()
      const maintenance = await getMaintenance()
      setMaintenance(maintenance.maintenance)

      const monthlyReports = await getMonthlyReports()
      setMonthlyReport(monthlyReports.reports)

      const dailyReports = await getDailyReports("", "")
      setDailyReport(dailyReports.reports)

      //テスト用
      //const expandedReports = Array(10).fill(monthlyReports.reports).flat(); // 10倍に複製
      //setMonthlyReport(expandedReports);
    } catch (error: any) {
      //console.log(error)
      navigate("/")
    } finally {
      stopLoading()
    }
  }
  useEffect(() => {
    fetchMaintenance()
  }, [])

  // メンテナンス状態の変更
  const handleMaintenanceChange = (e: any) => {
    setMaintenance(Number(e.target.value)) // 数値に変換して状態を更新
  }

  // メンテナンス更新
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      startLoading()
      await maintenanceUpdate(maintenance, maintenanceMemo)
    } catch (error: any) {
      //console.log(error)
      navigate("/")
    } finally {
      stopLoading()
    }
  }

  // 閲覧モード
  const fetchManageCatvList = async () => {
    try {
      startLoading()

      // サービス提供元一覧
      const manageCatvData = await getManageCatvList()
      setCatvData(manageCatvData.catv_list)

      // 不動産一覧
      const manageEstateData = await getManageEstateList(1)
      setEstateData(manageEstateData.estate_list)
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else if (!error.response) {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }

  // 閲覧モードの初期化
  useEffect(() => {
    fetchManageCatvList()
    const selectedCatv = localStorage.getItem("show_mode_catv")
    if (selectedCatv !== null) {
      setShowModeCatv(selectedCatv)
    }
    const selectedEstate = localStorage.getItem("show_mode_estate")
    if (selectedEstate !== null) {
      setShowModeEstate(selectedEstate)
    }
    const selectedCatvName = localStorage.getItem("show_mode_catv_name")
    if (selectedCatvName !== null) {
      setShowModeCatvName(selectedCatvName)
    }
    const selectedEstateName = localStorage.getItem("show_mode_estate_name")
    if (selectedEstateName !== null) {
      setShowModeEstateName(selectedEstateName)
    }
    const selectViewMode = localStorage.getItem("view_mode")
    if (selectViewMode !== null) {
      setViewMode(selectViewMode)
    }
  }, [])

  // 閲覧モードの初期化（不動産）
  useEffect(() => {
    // estateDataが揃ったタイミングでフィルタリングする
    if (showModeCatv && estateData.length > 0) {
      const filteredEstate = estateData.filter((estate) => `Catv|${estate.catv_id}` === showModeCatv)
      setFilteredModeEstate(filteredEstate)
    }
  }, [showModeCatv, estateData])

  // 閲覧モードの切り替え
  // サービス提供元
  const handleShowModeChangeCatv = (e: any) => {
    setShowModeCatv(e.target.value)
    const filteredEstate = estateData.filter((estate) => `Catv|${estate.catv_id}` === e.target.value)
    setFilteredModeEstate(filteredEstate)
    setShowModeEstate("-")

    const selectedText = e.target.selectedOptions[0].text
    setShowModeCatvName(selectedText)
    setShowModeEstateName("-")
  }
  const handleShowModeChangeEstate = (e: any) => {
    setShowModeEstate(e.target.value)
    const selectedText = e.target.selectedOptions[0].text
    setShowModeEstateName(selectedText)
  }
  const handleViewModeChange = (e: any) => {
    setViewMode(e.target.value)
    if (e.target.value === "0") {
      setShowModeCatv("-")
      setShowModeEstate("-")
      setShowModeCatvName("-")
      setShowModeEstateName("-")
    }
  }
  const handleShowModeChange = async () => {
    // 閲覧モードの切り替えをサーバーに通知
    const view_mode = Number(viewMode)

    // 閲覧モードや閲覧編集モードであるにも関わらず、view_mode === 0 の場合は処理を行わない
    if (view_mode !== 0) {
      if (showModeCatv === "-") {
        return
      }
    }

    if (view_mode === 2) {
      setShowPasswordModal(true)
      return
    }

    try {
      await changeViewMode(
        view_mode,
        showModeCatv === "-" ? "" : showModeCatv,
        showModeEstate === "-" ? "" : showModeEstate,
        ""
      )

      // 閲覧モード切替に成功した場合、フロント側でも表示用に一時メモリに保存
      localStorage.setItem("show_mode_catv", showModeCatv)
      localStorage.setItem("show_mode_estate", showModeEstate)
      localStorage.setItem("show_mode_catv_name", showModeCatvName)
      localStorage.setItem("show_mode_estate_name", showModeEstateName)
      localStorage.setItem("view_mode", view_mode.toString())
      //console.log(showModeCatvName)
      //console.log(showModeEstateName)

      // 閲覧モードへの切替を画面に反映
      window.location.reload()
    } catch (e) {
      console.log(e)
    }
  }
  const handleShowModeChangeClose = async (num: number) => {
    setShowPasswordModal(false)
    if (num == 1) {
      window.location.reload()
    }
  }

  // 検索処理
  const handleSearchDailyReport = async () => {
    startLoading()
    const dailyReports = await getDailyReports(searchStartDate, searchEndDate)
    setDailyReport(dailyReports.reports)
    stopLoading()
  }

  return (
    <DashboardLayout>
      {showPasswordModal && (
        <ManageAdminPassword
          showModeCatv={showModeCatv}
          showModeEstate={showModeEstate}
          showModeCatvName={showModeCatvName}
          showModeEstateName={showModeEstateName}
          onClose={handleShowModeChangeClose}
        />
      )}
      <MDBox mb={4}>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3} pb={3}>
        <MDBox>
          <MDTypography variant="h4" gutterBottom>
            管理機能
          </MDTypography>
        </MDBox>
      </MDBox>

      {/* メンテナンス状態変更 */}
      <Card sx={{ height: "100%", padding: "20px", marginBottom: "20px" }}>
        <MDTypography variant="h4" fontWeight="medium" sx={{ marginBottom: "20px", flexShrink: 1 }}>
          メンテナンス
        </MDTypography>
        <MDBox display="flex" flexWrap="wrap" gap={2}>
          <MDBox>
            <select name="buildingType" value={maintenance} onChange={handleMaintenanceChange}>
              <option value={0}>通常</option>
              <option value={1}>メンテナンス</option>
            </select>
          </MDBox>
          <MDInput
            label="メンテナンス情報"
            type="text"
            name="name"
            value={maintenanceMemo}
            onChange={(e) => setMaintenanceMemo(e.target.value)}
            sx={{ width: "calc(100% - 220px)" }}
          />
          <MDBox>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              変更
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
      <Card sx={{ height: "100%", padding: "20px" }}>
        <MDTypography variant="h4" fontWeight="medium" sx={{ marginBottom: "20px", flexShrink: 1 }}>
          閲覧モード
        </MDTypography>
        <MDBox display="flex" flexWrap="wrap" gap={2}>
          <select name="viewMode" value={viewMode} onChange={handleViewModeChange} style={{ maxWidth: "150px" }}>
            <option key={0} value={"0"}>
              通常
            </option>
            <option key={1} value={"1"}>
              閲覧モード
            </option>
            <option key={2} value={"2"}>
              閲覧編集モード
            </option>
          </select>
          <select
            name="showModeCatv"
            value={showModeCatv}
            onChange={handleShowModeChangeCatv}
            disabled={viewMode === "0" ? true : false}
          >
            <option key={0} value={"-"}>
              -
            </option>
            {catvData.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
          <select
            name="showModeEstate"
            value={showModeEstate}
            onChange={handleShowModeChangeEstate}
            disabled={viewMode === "0" ? true : false}
          >
            <option key={0} value={"-"}>
              -
            </option>
            {filteredModeEstate.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
          <MDBox>
            {viewMode !== "0" && showModeCatv === "-" && (
              <MDButton
                variant="contained"
                color="info"
                fullWidth
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "1rem",
                  padding: "0.75rem 0",
                  backgroundColor: "#1976d2"
                }}
                disabled
              >
                変更
              </MDButton>
            )}
            {!(viewMode !== "0" && showModeCatv === "-") && (
              <MDButton
                variant="contained"
                color="info"
                fullWidth
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "1rem",
                  padding: "0.75rem 0",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#1565c0"
                  }
                }}
                onClick={() => {
                  handleShowModeChange()
                }}
              >
                変更
              </MDButton>
            )}
          </MDBox>
        </MDBox>
      </Card>
      {/* 月次レポート */}
      <Card style={{ marginTop: "20px" }}>
        <MDBox style={{ fontSize: "1.25rem", fontWeight: "600" }} pt={3} px={3}>
          月次レポート
        </MDBox>
        <TableContainer sx={{ overflowX: "auto", overflowY: "auto", height: "200px", marginTop: "20px" }}>
          <Table stickyHeader>
            <TableHead sx={{ position: "sticky", display: "table-header-group", zIndex: 3 }}>
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    fontSize: "0.875rem",
                    color: "text.primary",
                    fontWeight: "bold",
                    backgroundColor: "#FFFFFF"
                  }
                }}
              >
                <TableCell align="left">ファイル名</TableCell>
                <TableCell align="left">年月</TableCell>
                <TableCell align="left">URL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthlyReport.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "0.875rem",
                      color: "text.primary",
                      fontWeight: "normal",
                      zIndex: 0
                    }
                  }}
                >
                  <TableCell align="left">
                    <div
                      style={{
                        minWidth: "200px",
                        width: "10vw",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={row.filename || undefined}
                    >
                      {row.filename}
                    </div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "100px" }}>{row.year_month}</div>
                  </TableCell>
                  <TableCell align="left">
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="medium"
                      onClick={() => window.open(row.url, "_blank")}
                    >
                      DL
                    </MDButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      {/* 日次レポート */}
      <Card style={{ marginTop: "20px" }}>
        <MDBox style={{ fontSize: "1.25rem", fontWeight: "600" }} pt={3} px={3}>
          日次レポート
        </MDBox>
        <MDBox display="flex" flexWrap="wrap" alignItems="left" gap={2} pt={3} px={3}>
          <MDInput
            type="date"
            value={searchStartDate}
            onChange={(e) => setSearchStartDate(e.target.value)}
            sx={{ width: "160px" }}
          />
          <span style={{ paddingTop: "6px" }}>〜</span>
          <MDInput
            type="date"
            value={searchEndDate}
            onChange={(e) => setSearchEndDate(e.target.value)}
            sx={{ width: "160px" }}
          />
          <MDButton variant="gradient" color="info" size="medium" onClick={handleSearchDailyReport}>
            <Icon>search</Icon>&nbsp; 検索
          </MDButton>
        </MDBox>
        <TableContainer sx={{ overflowX: "auto", overflowY: "auto", height: "200px", marginTop: "20px" }}>
          <Table stickyHeader>
            <TableHead sx={{ position: "sticky", display: "table-header-group", zIndex: 3 }}>
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    fontSize: "0.875rem",
                    color: "text.primary",
                    fontWeight: "bold",
                    backgroundColor: "#FFFFFF"
                  }
                }}
              >
                <TableCell align="left">ファイル名</TableCell>
                <TableCell align="left">年月日</TableCell>
                <TableCell align="left">URL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dailyReport.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "0.875rem",
                      color: "text.primary",
                      fontWeight: "normal",
                      zIndex: 0
                    }
                  }}
                >
                  <TableCell align="left">
                    <div
                      style={{
                        minWidth: "200px",
                        width: "10vw",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={row.filename || undefined}
                    >
                      {row.filename}
                    </div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "100px" }}>{row.date}</div>
                  </TableCell>
                  <TableCell align="left">
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="medium"
                      onClick={() => window.open(row.url, "_blank")}
                    >
                      DL
                    </MDButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </DashboardLayout>
  )
}

export default ManagePage
